import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Heart, Phone, IdCard, Baby, User, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { FileUploadButton } from "@/components/FileUploadButton";
import { formatError } from "@/lib/api";
import { useCollection } from "./useCollection";

const empty = {
  name: "", relation: "", date_of_birth: "", is_child: false,
  photo_file_id: null, passport_file_id: null, passport_number: "", passport_expiry: "",
  nationality: "", blood_type: "", allergies: "", medications: "", conditions: "",
  emergency_contact_name: "", emergency_contact_phone: "", child_notes: "",
};

export default function TravelersSection({ tripId, onChange }) {
  const { items, loading, create, update, remove } = useCollection(tripId, "travelers", onChange);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  function openNew() { setEditingId(null); setForm(empty); setOpen(true); }
  function openEdit(item) {
    setEditingId(item.id);
    setForm({ ...empty, ...Object.fromEntries(Object.entries(item).filter(([k]) => k in empty)) });
    setOpen(true);
  }
  function handleOpenChange(v) { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }

  async function save() {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editingId) { await update(editingId, form); toast.success("Traveler updated"); }
      else { await create(form); toast.success("Traveler added"); }
      handleOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div><h3 className="font-display text-xl">Travelers</h3><p className="text-sm text-stone-500">People on this trip — with medical & emergency info.</p></div>
        <Button className="rounded-full bg-primary text-primary-foreground" onClick={openNew} data-testid="add-traveler-btn"><Plus className="w-4 h-4 mr-2" />Add traveler</Button>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit traveler" : "New traveler"}</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2 flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden border border-border">
                  <AuthImage fileId={form.photo_file_id} className="w-full h-full object-cover" fallbackClassName="w-full h-full" />
                </div>
                <FileUploadButton label="Photo" accept="image/*" testId="traveler-photo-upload" onUploaded={(id) => setForm((f) => ({ ...f, photo_file_id: id }))} />
              </div>
              <div><Label>Name</Label><Input className="rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="traveler-name-input" /></div>
              <div><Label>Relation</Label><Input className="rounded-xl" value={form.relation} placeholder="Self, Spouse, Son..." onChange={(e) => setForm({ ...form, relation: e.target.value })} /></div>
              <div><Label>Date of birth</Label><Input type="text" inputMode="numeric" placeholder="YYYY-MM-DD" title="Format: YYYY-MM-DD" className="rounded-xl" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
              <div><Label>Nationality</Label><Input className="rounded-xl" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></div>
              <div className="md:col-span-2 flex items-center gap-2">
                <Checkbox id="is_child" checked={form.is_child} onCheckedChange={(v) => setForm({ ...form, is_child: !!v })} />
                <Label htmlFor="is_child">This traveler is a child</Label>
              </div>
              <div className="md:col-span-2 pt-2 text-xs uppercase tracking-widest text-stone-500">ID / Passport</div>
              <div><Label>Passport number</Label><Input className="rounded-xl" value={form.passport_number} onChange={(e) => setForm({ ...form, passport_number: e.target.value })} /></div>
              <div><Label>Passport expiry</Label><Input type="text" inputMode="numeric" placeholder="YYYY-MM-DD" title="Format: YYYY-MM-DD" className="rounded-xl" value={form.passport_expiry || ""} onChange={(e) => setForm({ ...form, passport_expiry: e.target.value })} /></div>
              <div className="md:col-span-2 flex items-center gap-3">
                <FileUploadButton label={form.passport_file_id ? "Change ID photo" : "Attach ID photo"} testId="traveler-passport-upload" onUploaded={(id) => setForm((f) => ({ ...f, passport_file_id: id }))} />
                {form.passport_file_id && <span className="text-xs text-stone-500">Attached</span>}
              </div>
              <div className="md:col-span-2 pt-2 text-xs uppercase tracking-widest text-stone-500">Medical</div>
              <div><Label>Blood type</Label><Input className="rounded-xl" value={form.blood_type} placeholder="O+, A-, ..." onChange={(e) => setForm({ ...form, blood_type: e.target.value })} /></div>
              <div><Label>Allergies</Label><Input className="rounded-xl" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} /></div>
              <div><Label>Medications</Label><Input className="rounded-xl" value={form.medications} onChange={(e) => setForm({ ...form, medications: e.target.value })} /></div>
              <div><Label>Conditions</Label><Input className="rounded-xl" value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} /></div>
              <div className="md:col-span-2 pt-2 text-xs uppercase tracking-widest text-stone-500">Emergency contact</div>
              <div><Label>Contact name</Label><Input className="rounded-xl" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
              <div><Label>Contact phone</Label><Input className="rounded-xl" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
              {form.is_child && (
                <div className="md:col-span-2"><Label>Child notes (school, guardian, etc.)</Label><Textarea className="rounded-xl" value={form.child_notes} onChange={(e) => setForm({ ...form, child_notes: e.target.value })} /></div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-full">Cancel</Button>
              <Button onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-traveler-btn">{saving ? "Saving..." : editingId ? "Save changes" : "Save traveler"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="h-32 rounded-2xl bg-muted animate-pulse" /> :
       items.length === 0 ? (
        <Card className="rounded-2xl p-10 border-dashed border-border text-center" data-testid="empty-travelers">
          <User className="w-8 h-8 mx-auto text-primary" />
          <p className="mt-2 text-stone-500">No travelers added yet.</p>
        </Card>
       ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <Card key={t.id} className="p-5 rounded-2xl border-border" data-testid={`traveler-${t.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl overflow-hidden border border-border shrink-0">
                  <AuthImage fileId={t.photo_file_id} className="w-full h-full object-cover" fallbackClassName="w-full h-full" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg truncate">{t.name}</div>
                    <div className="flex items-center gap-0.5">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(t)} className="rounded-full text-stone-400 hover:text-primary" data-testid={`edit-traveler-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(t.id)} className="rounded-full text-stone-400 hover:text-destructive" data-testid={`delete-traveler-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="text-xs text-stone-500">{t.relation || "Traveler"}{t.is_child && <span className="inline-flex items-center gap-1 ml-2 text-secondary"><Baby className="w-3 h-3" />Child</span>}</div>
                  {t.nationality && <div className="text-xs text-stone-500 mt-0.5">{t.nationality}{t.date_of_birth && ` · ${t.date_of_birth}`}</div>}
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                {(t.blood_type || t.allergies || t.medications || t.conditions) && (
                  <div className="p-3 rounded-xl bg-muted/60">
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-stone-500 mb-1"><Heart className="w-3 h-3" /> Medical</div>
                    {t.blood_type && <div>Blood: <b>{t.blood_type}</b></div>}
                    {t.allergies && <div>Allergies: {t.allergies}</div>}
                    {t.medications && <div>Meds: {t.medications}</div>}
                    {t.conditions && <div>Conditions: {t.conditions}</div>}
                  </div>
                )}
                {(t.passport_number || t.passport_file_id) && (
                  <div className="p-3 rounded-xl bg-muted/60">
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-stone-500 mb-1"><IdCard className="w-3 h-3" /> ID</div>
                    {t.passport_number && <div>Passport: {t.passport_number}</div>}
                    {t.passport_expiry && <div className="text-xs text-stone-500">Expires {t.passport_expiry}</div>}
                    {t.passport_file_id && <AuthImage fileId={t.passport_file_id} className="mt-2 w-full h-24 object-cover rounded-lg" />}
                  </div>
                )}
                {(t.emergency_contact_name || t.emergency_contact_phone) && (
                  <div className="p-3 rounded-xl bg-muted/60">
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-stone-500 mb-1"><Phone className="w-3 h-3" /> Emergency</div>
                    <div>{t.emergency_contact_name} {t.emergency_contact_phone && <span className="text-stone-500">· {t.emergency_contact_phone}</span>}</div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
       )}
    </div>
  );
}
